import { getTranslation } from '../languages';

export const generateDummyData = (uiLanguage = 'en') => {
    const t = (key) => getTranslation(key, uiLanguage);
    
    // Generate timestamp for "yesterday" and "2 days ago"
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dummyData = {
        topics: [
            {
                id: 'dummy-topic-1',
                name: t('tutorialDummyTopic1Name'),
                description: t('tutorialDummyTopic1Desc'),
                displayOrder: 1,
                isFavorite: true,
                isGeneratingTopic: false,
                contentLanguage: uiLanguage,
                hasUnread: true, // This topic has unread tips
                hasReviewDue: true, // This topic also has reviews due
                groupId: 'dummy-group-1', // Part of Personal Learning group
                isScheduled: true,
                scheduleTimeHour: 8,
                scheduleTimePeriod: 0, // AM
                scheduleFrequencyValue: 1,
                scheduleFrequencyUnit: 1, // Daily
                scheduledSkipIfUnread: true,
                learningSummary: `## Knowledge Profile

The learner demonstrates foundational understanding of basic health concepts with particular interest in hydration and energy management. Strong engagement with physiological aspects shows comprehension of how body systems interact, though questions reveal uncertainty about differentiating between thirst and dehydration symptoms. Knowledge gaps exist around optimal timing for health practices and the underlying circadian rhythm mechanisms. The learner recognizes connections between lifestyle factors and energy levels but seeks clearer guidance on practical implementation.

## Learning Patterns

The learner actively seeks practical, actionable information rather than theoretical knowledge, frequently asking "how" and "why" questions about daily health practices. Engagement patterns show preference for understanding cause-and-effect relationships and scientific reasoning behind health recommendations. Questions focus on personal application and troubleshooting specific challenges like afternoon energy crashes.

## Optimization Insights

Content that combines scientific explanations with immediate practical tips may resonate most effectively. Visual aids or charts for tracking patterns like hydration levels and energy fluctuations could enhance understanding and retention.`
            },
            {
                id: 'dummy-topic-2',
                name: t('tutorialDummyTopic2Name'),
                description: t('tutorialDummyTopic2Desc'),
                displayOrder: 2,
                isFavorite: false,
                isGeneratingTopic: false,
                contentLanguage: uiLanguage,
                hasUnread: false,
                hasReviewDue: true, // This topic has reviews due
                groupId: 'dummy-group-2' // Part of Hobbies group
            },
            {
                id: 'dummy-topic-3',
                name: t('tutorialDummyTopic3Name'),
                description: t('tutorialDummyTopic3Desc'),
                displayOrder: 3,
                isFavorite: false,
                isGeneratingTopic: false,
                contentLanguage: uiLanguage,
                hasUnread: false,
                hasReviewDue: false,
                groupId: 'dummy-group-2' // Part of Hobbies group
            }
        ],
        
        tips: [
            {
                id: 'dummy-tip-1',
                topicId: 'dummy-topic-1',
                title: t('tutorialDummyTip1Title'),
                content: t('tutorialDummyTip1Content'),  // Changed from description to content to match real data structure
                createdAt: twoDaysAgo.toISOString(),
                lastRead: null,
                readCount: 0,
                knowledgeStatus: null, // Unread status
                isFavorite: false,
                reviewIsDue: true, // All tips have review due in tutorial
                isUnread: true,
                isScheduledForReview: false
            },
            {
                id: 'dummy-tip-2',
                topicId: 'dummy-topic-1',
                title: t('tutorialDummyTip2Title'),
                content: t('tutorialDummyTip2Content'),  // Changed from description to content
                createdAt: yesterday.toISOString(),
                lastRead: yesterday.toISOString(),
                readCount: 1,
                knowledgeStatus: 'partial', // Read status - "partially know"
                dateLearned: yesterday.toISOString(), // When it was learned
                isFavorite: true,
                reviewIsDue: false, // Review not due yet
                isUnread: false,
                isScheduledForReview: true, // This one is scheduled for review
                hasReviewQuestions: true, // Has review questions
                reviewCount: 2, // Has completed 2 reviews (day 1 and day 3)
                nextReviewDueDate: tomorrow.toISOString() // Next review is tomorrow (day 7)
            },
            {
                id: 'dummy-tip-3',
                topicId: 'dummy-topic-2',
                title: t('tutorialDummyTip3Title'),
                content: t('tutorialDummyTip3Content'),  // Changed from description to content
                createdAt: twoDaysAgo.toISOString(),
                lastRead: twoDaysAgo.toISOString(),
                readCount: 2,
                knowledgeStatus: 'known', // Read status - "already known"
                isFavorite: false,
                reviewIsDue: true, // All tips have review due in tutorial
                isUnread: false,
                isScheduledForReview: false
            }
        ],
        
        conversations: [
            {
                id: 'dummy-conv-1',
                tipId: 'dummy-tip-1',
                messages: [
                    {
                        role: 'bot',
                        text: t('tutorialDummyBotInit1')  // Bot initiates conversation
                    },
                    {
                        role: 'user',
                        text: t('tutorialDummyQuestion1')
                    },
                    {
                        role: 'bot',
                        text: t('tutorialDummyAnswer1')
                    }
                ],
                createdAt: yesterday.toISOString()
            },
            {
                id: 'dummy-conv-2',
                tipId: 'dummy-tip-2',
                messages: [
                    {
                        role: 'bot',
                        text: t('tutorialDummyBotInit2')  // Bot initiates conversation
                    },
                    {
                        role: 'user',
                        text: t('tutorialDummyQuestion2')
                    },
                    {
                        role: 'bot',
                        text: t('tutorialDummyAnswer2')
                    }
                ],
                createdAt: yesterday.toISOString()
            }
        ],
        
        reviewQuestions: [
            {
                id: 'dummy-review-1',
                tipId: 'dummy-tip-2',
                day: 1,  // Changed from reviewDay to day to match expected format
                reviewDay: 1,
                unlocked: true,
                question: 'What is the most reliable indicator that you are properly hydrated?',
                choices: [
                    'You never feel thirsty',
                    'Your urine is pale yellow',
                    'You drink exactly 8 glasses per day',
                    'You don\'t feel tired'
                ],
                correctAnswer: 1,
                explanation: 'Correct! Pale yellow urine (like lemonade) is the most reliable indicator of proper hydration.',
                explanations: {
                    0: 'Thirst is actually a late indicator - you\'re already mildly dehydrated when you feel thirsty.',
                    1: 'Correct! Pale yellow urine (like lemonade) is the most reliable indicator of proper hydration.',
                    2: 'The 8 glasses rule is a myth - individual needs vary based on size, activity, and climate.',
                    3: 'Fatigue has many causes beyond dehydration, making it an unreliable indicator.'
                },
                chatStarters: [  // Changed to array format
                    "Thirst is tricky! Let me explain why waiting until you're thirsty isn't ideal...",
                    "Exactly right! Want to learn about other hydration indicators to watch for?",
                    "The 8 glasses rule is surprisingly controversial. Let me share what research actually shows...",
                    "Fatigue and hydration are connected, but it's more complex. Let me explain..."
                ],
                selectedAnswer: 1,  // User selected the correct answer
                confidence: 'confident',  // User was confident
                wasCorrect: true,  // The answer was correct
                answeredAt: twoDaysAgo.toISOString(),  // Answered 2 days ago
                chatHistory: []
            },
            {
                id: 'dummy-review-2',
                tipId: 'dummy-tip-2',
                day: 3,  // Added day field
                reviewDay: 3,
                unlocked: true,
                question: t('tutorialDummyReviewQ2'),
                choices: [
                    t('tutorialDummyReviewChoice2A'),
                    t('tutorialDummyReviewChoice2B'),
                    t('tutorialDummyReviewChoice2C'),
                    t('tutorialDummyReviewChoice2D')
                ],
                correctAnswer: 2,
                explanation: t('tutorialDummyReviewExp2C'),
                explanations: {
                    0: t('tutorialDummyReviewExp2A'),
                    1: t('tutorialDummyReviewExp2B'),
                    2: t('tutorialDummyReviewExp2C'),
                    3: t('tutorialDummyReviewExp2D')
                },
                chatStarters: [  // Changed to array format
                    t('tutorialChatStarter2A'),
                    t('tutorialChatStarter2B'),
                    t('tutorialChatStarter2C'),
                    t('tutorialChatStarter2D')
                ],
                selectedAnswer: null,  // Not answered yet
                confidence: null,  // No confidence selected
                wasCorrect: null,  // Not answered
                answeredAt: null,  // Not answered
                chatHistory: []
            },
            {
                id: 'dummy-review-3',
                tipId: 'dummy-tip-2',
                day: 7,  // Added day field
                reviewDay: 7,
                unlocked: false,
                question: null,
                choices: [],
                correctAnswer: null,
                explanation: null,
                explanations: {},
                chatStarters: [],  // Changed to array format
                selectedAnswer: null,
                confidence: null,
                wasCorrect: null,
                answeredAt: null,
                chatHistory: []
            },
            {
                id: 'dummy-review-4',
                tipId: 'dummy-tip-2',
                day: 14,
                reviewDay: 14,
                unlocked: false,
                question: null,
                choices: [],
                correctAnswer: null,
                explanation: null,
                explanations: {},
                chatStarters: [],
                selectedAnswer: null,
                confidence: null,
                wasCorrect: null,
                answeredAt: null,
                chatHistory: []
            },
            {
                id: 'dummy-review-5',
                tipId: 'dummy-tip-2',
                day: 30,
                reviewDay: 30,
                unlocked: false,
                question: null,
                choices: [],
                correctAnswer: null,
                explanation: null,
                explanations: {},
                chatStarters: [],
                selectedAnswer: null,
                confidence: null,
                wasCorrect: null,
                answeredAt: null,
                chatHistory: []
            },
            {
                id: 'dummy-review-6',
                tipId: 'dummy-tip-2',
                day: 60,
                reviewDay: 60,
                unlocked: false,
                question: null,
                choices: [],
                correctAnswer: null,
                explanation: null,
                explanations: {},
                chatStarters: [],
                selectedAnswer: null,
                confidence: null,
                wasCorrect: null,
                answeredAt: null,
                chatHistory: []
            }
        ],
        
        // Topic Groups for tutorial
        topicGroups: [
            {
                id: 'dummy-group-1',
                name: t('tutorialDummyGroup1Name'),
                displayOrder: 1,
                createdAt: twoDaysAgo.toISOString()
            },
            {
                id: 'dummy-group-2',
                name: t('tutorialDummyGroup2Name'),
                displayOrder: 2,
                createdAt: yesterday.toISOString()
            }
        ],
        
        // Tutorial-specific metadata
        tutorialMetadata: {
            selectedTopicId: 'dummy-topic-1',
            selectedTipId: 'dummy-tip-1',
            activeConversationId: 'dummy-conv-1',
            unreadTopics: new Set(['dummy-topic-1']),
            reviewDueTopics: new Set(['dummy-topic-2'])
        }
    };
    
    return dummyData;
};